/* Search drawer — "Corner Search".
 *
 * Self-contained replacement for the orphaned predictive-search machinery.
 * Relies on global.js helpers (trapFocus, removeTrapFocus, debounce) and the
 * `routes.predictive_search_url` global from theme.liquid.
 *
 * Live results come from the Section Rendering API: the predictive search
 * endpoint rendered through sections/search-drawer-results.liquid, so all
 * markup and money formatting stay in Liquid.
 *
 * localStorage keys (both wrapped in try/catch — private mode etc. degrades
 * to a drawer without history, never a broken drawer):
 *   skf:recent-searches  — array of strings, newest first, max 6
 *   skf:recently-viewed  — array of {handle,title,url,image,price}, max 8
 */

class SearchDrawer extends HTMLElement {
	constructor() {
		super();

		this.RECENT_KEY = "skf:recent-searches";
		this.VIEWED_KEY = "skf:recently-viewed";
		this.RECENT_MAX = 6;
		this.VIEWED_MAX = 8;

		this.panel = this.querySelector(".search-drawer__panel");
		this.form = this.querySelector(".search-drawer__form");
		this.input = this.querySelector(".search-drawer__input");
		this.defaultView = this.querySelector("[data-sd-default]");
		this.resultsView = this.querySelector("[data-sd-results]");
		this.statusEl = this.querySelector(".search-drawer__status");

		this.cachedResults = {};
		this.abortController = null;
		this.activeElement = null;
		this.isOpen = false;

		this.recordViewedProduct();
		this.setupOpeners();
		this.setupEvents();
	}

	/* ---- wiring ---------------------------------------------------------- */

	setupOpeners() {
		document.querySelectorAll("[data-search-open]").forEach((opener) => {
			opener.setAttribute("role", "button");
			opener.setAttribute("aria-haspopup", "dialog");
		});

		// Delegated so openers survive theme-editor section reloads.
		document.addEventListener("click", (event) => {
			const opener = event.target.closest("[data-search-open]");
			if (!opener) return;
			event.preventDefault();
			this.open(opener);
		});
	}

	setupEvents() {
		this.querySelectorAll("[data-sd-close]").forEach((el) =>
			el.addEventListener("click", () => this.close())
		);

		// Document-level so Escape works no matter where focus sits; keydown
		// because it's delivered before the input's native Esc handling.
		// key, not code: virtual keyboards and synthetic events often omit code.
		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && this.isOpen) {
				event.preventDefault();
				this.close();
			}
		});

		this.input.addEventListener(
			"input",
			debounce(() => this.onInput(), 250)
		);

		this.form.addEventListener("submit", (event) => {
			const term = this.getQuery();
			if (!term.length) {
				event.preventDefault();
				return;
			}
			this.saveRecentSearch(term);
		});

		// Explicit Enter -> submit: not every keyboard (virtual, remote,
		// automated) triggers the browser's implicit form submission.
		this.input.addEventListener("keydown", (event) => {
			if (event.key !== "Enter") return;
			event.preventDefault();
			if (this.getQuery().length) this.form.requestSubmit();
		});

		// Save the term when a result or "search for" link is followed, and
		// the clicked suggestion text when a suggestion chip is followed.
		this.resultsView.addEventListener("click", (event) => {
			const link = event.target.closest("a");
			if (!link) return;
			if (link.hasAttribute("data-sd-save-term")) {
				this.saveRecentSearch(link.getAttribute("data-sd-save-term"));
			} else if (link.hasAttribute("data-sd-save-current-term")) {
				this.saveRecentSearch(this.getQuery());
			}
		});

		const clearButton = this.querySelector("[data-sd-clear-recent]");
		if (clearButton) {
			clearButton.addEventListener("click", () => {
				this.writeStore(this.RECENT_KEY, []);
				this.renderRecentSearches();
			});
		}
	}

	/* ---- open / close ---------------------------------------------------- */

	open(triggeredBy) {
		if (triggeredBy) this.activeElement = triggeredBy;
		this.renderRecentSearches();
		this.renderRecentlyViewed();

		setTimeout(() => {
			this.classList.add("animate", "active");
			// Not gated on transitionend: with reduced motion no transition
			// runs, and the keyboard must land in the drawer either way.
			trapFocus(this.panel, this.input);
		});
		document.body.classList.add("overflow-hidden");
		this.isOpen = true;
	}

	close() {
		if (!this.isOpen) return;
		this.classList.remove("active");
		document.body.classList.remove("overflow-hidden");
		this.isOpen = false;
		removeTrapFocus(this.activeElement);
	}

	/* ---- live results ---------------------------------------------------- */

	getQuery() {
		return this.input.value.trim();
	}

	onInput() {
		const term = this.getQuery();

		if (!term.length) {
			this.showDefault();
			return;
		}

		this.getSearchResults(term);
	}

	showDefault() {
		if (this.abortController) this.abortController.abort();
		this.resultsView.hidden = true;
		this.defaultView.hidden = false;
		this.classList.remove("loading");
	}

	getSearchResults(term) {
		const cacheKey = term.toLowerCase();

		if (this.cachedResults[cacheKey]) {
			this.renderResults(this.cachedResults[cacheKey], term);
			return;
		}

		if (this.abortController) this.abortController.abort();
		this.abortController = new AbortController();
		this.classList.add("loading");
		this.setStatus(this.getAttribute("data-loading-text") || "Loading");

		const url =
			`${routes.predictive_search_url}?q=${encodeURIComponent(term)}` +
			`&resources[type]=product,query,collection` +
			`&resources[limit]=8&resources[limit_scope]=each` +
			`&section_id=search-drawer-results`;

		fetch(url, { signal: this.abortController.signal })
			.then((response) => {
				if (!response.ok) throw new Error(response.status);
				return response.text();
			})
			.then((text) => {
				const markup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-search-drawer-results").innerHTML;
				this.cachedResults[cacheKey] = markup;
				// Only render if the term is still what's in the box.
				if (this.getQuery().toLowerCase() === cacheKey) {
					this.renderResults(markup, term);
				}
			})
			.catch((error) => {
				if (error.name === "AbortError") return;
				// Keep the form usable: show the default view; Enter still
				// submits to /search.
				this.classList.remove("loading");
				this.setStatus("");
				this.showDefault();
			});
	}

	renderResults(markup, term) {
		this.resultsView.innerHTML = markup;
		this.resultsView.hidden = false;
		this.defaultView.hidden = true;
		this.classList.remove("loading");

		const countEl = this.resultsView.querySelector("[data-sd-count]");
		const count = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;
		this.setStatus(
			count > 0 ? `${count} results for ${term}` : `No results for ${term}`
		);
	}

	setStatus(text) {
		if (!this.statusEl) return;
		this.statusEl.setAttribute("aria-hidden", "false");
		this.statusEl.textContent = text;
		setTimeout(() => this.statusEl.setAttribute("aria-hidden", "true"), 1000);
	}

	/* ---- localStorage: recent searches ----------------------------------- */

	readStore(key) {
		try {
			const parsed = JSON.parse(window.localStorage.getItem(key));
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			return [];
		}
	}

	writeStore(key, value) {
		try {
			window.localStorage.setItem(key, JSON.stringify(value));
		} catch (error) {
			/* storage unavailable — history simply doesn't persist */
		}
	}

	saveRecentSearch(term) {
		term = (term || "").trim();
		if (!term.length) return;
		const existing = this.readStore(this.RECENT_KEY).filter(
			(t) => typeof t === "string" && t.toLowerCase() !== term.toLowerCase()
		);
		existing.unshift(term);
		this.writeStore(this.RECENT_KEY, existing.slice(0, this.RECENT_MAX));
	}

	renderRecentSearches() {
		const group = this.querySelector("[data-sd-recent]");
		const list = this.querySelector("[data-sd-recent-list]");
		if (!group || !list) return;

		const terms = this.readStore(this.RECENT_KEY).filter(
			(t) => typeof t === "string" && t.trim().length
		);
		list.textContent = "";

		terms.slice(0, this.RECENT_MAX).forEach((term) => {
			const li = document.createElement("li");
			const link = document.createElement("a");
			link.className = "search-drawer__suggestion";
			link.href = `${routes.search_url}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
			link.textContent = term;
			li.appendChild(link);
			list.appendChild(li);
		});

		group.hidden = terms.length === 0;
	}

	/* ---- localStorage: recently viewed ------------------------------------ */

	recordViewedProduct() {
		const dataEl = document.querySelector("[data-sd-viewed-product]");
		if (!dataEl) return;

		let entry;
		try {
			entry = JSON.parse(dataEl.textContent);
		} catch (error) {
			return;
		}
		if (!entry || typeof entry.handle !== "string" || !entry.handle.length) return;

		const existing = this.readStore(this.VIEWED_KEY).filter(
			(p) => p && p.handle !== entry.handle
		);
		existing.unshift(entry);
		this.writeStore(this.VIEWED_KEY, existing.slice(0, this.VIEWED_MAX));
	}

	renderRecentlyViewed() {
		const group = this.querySelector("[data-sd-viewed]");
		const list = this.querySelector("[data-sd-viewed-list]");
		if (!group || !list) return;

		const products = this.readStore(this.VIEWED_KEY).filter(
			(p) =>
				p &&
				typeof p.handle === "string" &&
				typeof p.title === "string" &&
				typeof p.url === "string"
		);
		list.textContent = "";

		// On a product page, don't lead with the product being looked at.
		const currentHandle = this.currentProductHandle();

		products
			.filter((p) => p.handle !== currentHandle)
			.slice(0, this.VIEWED_MAX)
			.forEach((product) => {
				const li = document.createElement("li");
				li.className = "search-drawer__product";

				const link = document.createElement("a");
				link.className = "search-drawer__product-link";
				link.href = product.url;

				const media = document.createElement("span");
				media.className = "search-drawer__product-media";
				if (typeof product.image === "string" && product.image.length) {
					const img = document.createElement("img");
					img.src = product.image;
					img.alt = "";
					img.width = 90;
					img.loading = "lazy";
					media.appendChild(img);
				}

				const title = document.createElement("span");
				title.className = "search-drawer__product-title";
				title.textContent = product.title;

				const price = document.createElement("span");
				price.className = "search-drawer__product-price";
				price.textContent = typeof product.price === "string" ? product.price : "";

				link.appendChild(media);
				link.appendChild(title);
				link.appendChild(price);
				li.appendChild(link);
				list.appendChild(li);
			});

		group.hidden = list.children.length === 0;
	}

	currentProductHandle() {
		const dataEl = document.querySelector("[data-sd-viewed-product]");
		if (!dataEl) return null;
		try {
			return JSON.parse(dataEl.textContent).handle || null;
		} catch (error) {
			return null;
		}
	}
}

customElements.define("search-drawer", SearchDrawer);
